/**
 * IXD 프리뷰 렌더러용 셀 선택 훅.
 *
 * 실제 선택/키보드/클립보드 동작은 공통 DOM 테이블 셀 선택 훅을 사용하고,
 * IXD 호출부가 기대하는 callback shape만 유지한다.
 */

import type { RefObject } from "react";
import {
  useTableCellSelection,
  type TableCellPasteEntry,
  type TableCellPasteResult,
  type TableCellSelectionEntry,
} from "@/hooks/use-table-cell-selection";

export const IXD_SELECTION_STYLE_ID = "ixd-cell-selection-style";

/**
 * IXD 셀이 현재 선택돼 있는지 여부.
 *
 * `useTableCellSelection`은 선택 상태를 동적 `<style>` element에 셀렉터로 적어 시각화한다.
 * 선택이 비어 있을 때는 textContent가 `""`로 비워진다. 다른 모듈(예: DSD 키보드 navigation)
 * 이 IXD selection 우선순위를 판단할 때 이 헬퍼를 사용한다.
 */
export const hasActiveIxdSelection = (): boolean => {
  const styleEl = document.getElementById(IXD_SELECTION_STYLE_ID);
  return !!styleEl?.textContent;
};

interface UseIxdCellSelectionOptions {
  /** Enter 키 → 해당 셀 편집 모드 진입 */
  onEnterEdit?: (cellId: string, uniqueId?: string) => void;
  /** 붙여넣기 시 (대상 td element 포함) 항목 전달 — data-cell-id는 unique하지 않으므로 element로 대상을 특정한다 */
  onPasteValues?: (entries: TableCellPasteEntry[]) => TableCellPasteResult;
  /** Backspace/Delete 시 선택된 셀 (element 포함) 목록 전달 */
  onDeleteValues?: (cells: TableCellSelectionEntry[]) => void;
  /** Ctrl+Z 되돌리기 */
  onUndo?: () => void;
  /** 선택 영역 변경 시 uniqueId 목록 전달 */
  onSelectionChange?: (uniqueIds: string[]) => void;
  /** 같은 metadata identity / cell id를 가진 관련 셀까지 함께 강조 */
  enableRelatedCellSelection?: boolean;
  /** 선택 배경 강조를 끈다. 검토 모드는 자체 강조 스타일을 별도로 입힌다. */
  disableSelectionStyle?: boolean;
}

export function useIxdCellSelection(
  containerRef: RefObject<HTMLDivElement | null>,
  options?: UseIxdCellSelectionOptions,
) {
  return useTableCellSelection(containerRef, {
    styleId: IXD_SELECTION_STYLE_ID,
    onEnterEdit: (cellId, uniqueId) => options?.onEnterEdit?.(cellId, uniqueId),
    onPasteValues: options?.onPasteValues,
    onDeleteValues: options?.onDeleteValues
      ? (_cellIds, cells) => options.onDeleteValues?.(cells)
      : undefined,
    onUndo: options?.onUndo,
    onSelectionChange: options?.onSelectionChange
      ? (uniqueIds) => options.onSelectionChange?.(uniqueIds)
      : undefined,
    enableRelatedCellSelection: options?.enableRelatedCellSelection,
    disableSelectionStyle: options?.disableSelectionStyle,
  });
}
